import * as THREE from 'three';
import gsap from 'gsap';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/*
 * Carrousel de modèles 3D (section .accessoires)
 *
 * Principe : les cases grises (.model_carousel__item) sont du HTML/CSS normal
 * et c'est le JS qui les déplace. Le canvas .webgl couvre toute la section
 * (sans capter la souris) et, à chaque image, chaque modèle 3D est placé
 * par-dessus la case qui lui correspond.
 */

// Largeur du conteneur dans la maquette Figma (les emplacements sont en px de cette maquette)
const DESIGN_WIDTH = 995;

// Les 5 emplacements de la maquette, dans l'ordre du « tour » du carrousel :
// centre -> droite -> haut droite -> haut gauche -> gauche -> centre...
const SLOTS = [
  { x: 310, y: 170, size: 372 }, // 0 : centre (modèle courant)
  { x: 761, y: 118, size: 234 }, // 1 : droite
  { x: 547, y: 0, size: 146 }, // 2 : haut droite
  { x: 294, y: 0, size: 146 }, // 3 : haut gauche
  { x: 0, y: 118, size: 233 }, // 4 : gauche
];

const FOV = 30; // champ de vision de la caméra (degrés)
const FIT = 0.8; // part de la case occupée par le modèle
const TILT = 0.5; // inclinaison du modèle vers la caméra (radians)

export default class ModelCarousel {
  constructor(element) {
    this.element = element;
    this.section = element.closest('.accessoires');
    this.canvas = this.section ? this.section.querySelector('.webgl') : null;
    this.info = this.section ? this.section.querySelector('.info') : null;
    this.items = Array.from(element.querySelectorAll('.model_carousel__item'));
    // Modèle par défaut du carrousel (data-model sur .model_carousel)...
    this.modelUrl = element.dataset.model || 'assets/models/scene.gltf';
    // ...que chaque case peut remplacer avec son propre data-model
    this.itemUrls = this.items.map(
      (item) => item.dataset.model || this.modelUrl,
    );

    if (!this.canvas || !this.info || this.items.length !== SLOTS.length) {
      console.warn(
        `ModelCarousel : il faut un canvas .webgl, une div .info et exactement ${SLOTS.length} éléments .model_carousel__item.`,
      );
      return;
    }

    this.count = SLOTS.length;
    this.current = 0; // index du modèle au centre
    this.progress = 0; // position du « tour » (décimale pendant l'animation)
    this.hovered = -1; // index du modèle survolé
    this.visible = false; // la section est-elle à l'écran ?
    this.width = 0;
    this.height = 0;
    this.parts = this.items.map(() => null); // les groupes 3D de chaque modèle (null tant qu'il n'est pas chargé)
    this.pointer = { x: 0, y: 0 };
    this.reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    this.tick = this.tick.bind(this);
    this.init();
  }

  init() {
    this.createRenderer();
    this.createScene();
    this.loadModels();
    this.cacheInfo();
    this.bindEvents();
    this.layout();
    this.updateStates();
    this.updateInfo(true);
    this.observe();

    // On dessine dans le même ticker que GSAP (ScrollSmoother) pour rester synchro avec le défilement
    gsap.ticker.add(this.tick);
  }

  /* ---------- Three.js ---------- */

  createRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      alpha: true,
      antialias: true,
    });
    this.renderer.setClearColor(0x000000, 0);
  }

  createScene() {
    this.scene = new THREE.Scene();

    // 1 unité = 1 px à z = 0 (voir resize), ce qui permet de placer les modèles au pixel près
    this.camera = new THREE.PerspectiveCamera(FOV, 1, 10, 20000);

    const ambient = new THREE.AmbientLight('#ffffff', 1.6);
    const key = new THREE.DirectionalLight('#ffffff', 2.6);
    key.position.set(-1, 2, 3);
    const fill = new THREE.DirectionalLight('#ffffff', 0.8);
    fill.position.set(2, 0.5, 1);
    this.scene.add(ambient, key, fill);
  }

  // Charge chaque fichier une seule fois, même si plusieurs cases l'utilisent
  loadModels() {
    const loader = new GLTFLoader();
    const urls = [...new Set(this.itemUrls)];

    urls.forEach((url) => {
      loader.load(
        url,
        (gltf) => this.createModels(url, gltf.scene),
        undefined,
        (error) => {
          console.warn(
            `ModelCarousel : impossible de charger ${url} (il manque peut-être scene.bin ou le dossier textures/ à côté du .gltf). Un modèle de remplacement est affiché.`,
            error,
          );
          this.createModels(url, this.createFallback());
        },
      );
    });
  }

  // Forme de secours si le modèle ne se charge pas
  createFallback() {
    return new THREE.Mesh(
      new THREE.TorusKnotGeometry(0.3, 0.1, 160, 24),
      new THREE.MeshStandardMaterial({
        color: '#6c717a',
        roughness: 0.4,
        metalness: 0.1,
      }),
    );
  }

  // Crée un clone du modèle pour chaque case qui utilise cette url
  createModels(url, source) {
    source.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(source);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;

    this.items.forEach((item, index) => {
      if (this.itemUrls[index] !== url) return;

      const model = source.clone();
      model.position.copy(center).multiplyScalar(-1); // centre le modèle sur son milieu

      const normalized = new THREE.Group(); // ramène le modèle à une taille de 1
      normalized.scale.setScalar(1 / maxDim);
      normalized.add(model);

      const spinner = new THREE.Group(); // rotation continue
      spinner.rotation.y = index * 1.2;
      spinner.add(normalized);

      const tilt = new THREE.Group(); // inclinaison + effet au survol
      tilt.rotation.x = TILT;
      tilt.add(spinner);

      const pivot = new THREE.Group(); // position et taille en px
      pivot.add(tilt);
      this.scene.add(pivot);

      this.parts[index] = { pivot, tilt, spinner, speed: 0 };
    });
  }

  resize() {
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    if (!width || !height) return;

    this.width = width;
    this.height = height;

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(width, height, false);

    this.camera.aspect = width / height;
    this.camera.position.z =
      height / 2 / Math.tan(THREE.MathUtils.degToRad(FOV / 2));
    this.camera.updateProjectionMatrix();

    this.layout();
  }

  tick(time, deltaTime) {
    if (!this.visible || !this.width) return;

    const dt = Math.min(deltaTime, 100) / 1000;
    const canvasRect = this.canvas.getBoundingClientRect();

    for (let i = 0; i < this.items.length; i++) {
      const part = this.parts[i];
      if (!part) continue; // ce modèle n'est pas encore chargé
      const { pivot, tilt, spinner } = part;
      const rect = this.items[i].getBoundingClientRect();

      // Place le modèle sur sa case (coordonnées du canvas -> coordonnées de la scène)
      const x = rect.left - canvasRect.left + rect.width / 2;
      const y = rect.top - canvasRect.top + rect.height / 2;
      pivot.position.set(x - this.width / 2, this.height / 2 - y, 0);
      pivot.scale.setScalar(Math.min(rect.width, rect.height) * FIT);

      // Rotation continue : plus rapide pour le modèle courant
      const isCurrent = i === this.current;
      const targetSpeed = this.reduceMotion ? 0 : isCurrent ? 0.6 : 0.3;
      part.speed += (targetSpeed - part.speed) * 0.05;
      spinner.rotation.y += part.speed * dt;

      // Le modèle courant s'incline vers la souris quand on le survole
      let tiltX = TILT;
      let tiltY = 0;
      if (isCurrent && this.hovered === i && !this.reduceMotion) {
        const nx = ((this.pointer.x - rect.left) / rect.width) * 2 - 1;
        const ny = ((this.pointer.y - rect.top) / rect.height) * 2 - 1;
        tiltX += ny * 0.3;
        tiltY = nx * 0.3;
      }
      tilt.rotation.x += (tiltX - tilt.rotation.x) * 0.1;
      tilt.rotation.y += (tiltY - tilt.rotation.y) * 0.1;
    }

    this.renderer.render(this.scene, this.camera);
  }

  /* ---------- Carrousel (cases HTML) ---------- */

  // Position d'une case pour une position de « tour » donnée.
  // Entre deux emplacements, on interpole pour que la case glisse de l'un à l'autre.
  static getSlot(index, progress, count) {
    const p = (((index - progress) % count) + count) % count;
    const from = Math.floor(p);
    const t = p - from;
    const a = SLOTS[from % count];
    const b = SLOTS[(from + 1) % count];

    return {
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
      size: a.size + (b.size - a.size) * t,
    };
  }

  // Plus court chemin (de -2 à +2 pas) pour amener `index` au centre
  static getShortestStep(index, progress, count) {
    const half = count / 2;
    return ((((index - progress + half) % count) + count) % count) - half;
  }

  layout() {
    const scale = this.element.clientWidth / DESIGN_WIDTH;

    for (let i = 0; i < this.items.length; i++) {
      const { x, y, size } = ModelCarousel.getSlot(i, this.progress, this.count);
      const item = this.items[i];

      item.style.width = `${size * scale}px`;
      item.style.height = `${size * scale}px`;
      item.style.transform = `translate3d(${x * scale}px, ${y * scale}px, 0)`;
      item.style.zIndex = Math.round(size);
    }
  }

  goTo(index) {
    const target = ((index % this.count) + this.count) % this.count;
    if (target === this.current) return;

    const step = ModelCarousel.getShortestStep(
      target,
      this.progress,
      this.count,
    );

    this.current = target;
    this.updateStates();
    this.updateInfo();

    gsap.killTweensOf(this, 'progress');
    gsap.to(this, {
      progress: this.progress + step,
      duration: this.reduceMotion ? 0 : 0.5 + Math.abs(step) * 0.25,
      ease: 'power3.inOut',
      onUpdate: () => this.layout(),
      onComplete: () => this.syncHover(),
    });
  }

  next() {
    this.goTo(this.current + 1);
  }

  prev() {
    this.goTo(this.current - 1);
  }

  updateStates() {
    this.items.forEach((item, i) => {
      const isCurrent = i === this.current;
      item.classList.toggle('is-current', isCurrent);
      if (isCurrent) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  }

  /* ---------- Texte de la div .info ---------- */

  cacheInfo() {
    this.infoContent = this.info.querySelector('.info__content');
    this.infoPhrase = this.info.querySelector('.info__phrase');
    this.infoName = this.info.querySelector('.info__name');
    this.infoText = this.info.querySelector('.info__text');
    this.infoMore = this.info.querySelector('.info__more');
    this.shown = null;
  }

  // Affiche les infos du modèle courant (data-phrase, data-name, data-description, data-href)
  updateInfo(instant = false) {
    const data = this.items[this.current].dataset;
    const next = {
      phrase: data.phrase || '',
      name: data.name || '',
      text: data.description || '',
      href: data.href || '#',
    };

    const prev = this.shown;
    if (
      prev &&
      prev.phrase === next.phrase &&
      prev.name === next.name &&
      prev.text === next.text &&
      prev.href === next.href
    ) {
      return;
    }
    this.shown = next;

    const write = () => {
      this.infoPhrase.textContent = next.phrase;
      this.infoName.textContent = next.name;
      this.infoText.textContent = next.text;
      this.infoMore.setAttribute('href', next.href);
      this.infoMore.setAttribute('aria-label', `Voir ${next.name}`);
    };

    gsap.killTweensOf(this.infoContent);

    if (instant || this.reduceMotion) {
      write();
      gsap.set(this.infoContent, { autoAlpha: 1, y: 0 });
      return;
    }

    gsap.to(this.infoContent, {
      autoAlpha: 0,
      y: 8,
      duration: 0.15,
      ease: 'power1.in',
      onComplete: () => {
        write();
        gsap.to(this.infoContent, {
          autoAlpha: 1,
          y: 0,
          duration: 0.3,
          ease: 'power2.out',
        });
      },
    });
  }

  /* ---------- Événements ---------- */

  bindEvents() {
    this.items.forEach((item, index) => {
      item.addEventListener('click', () => this.goTo(index));
    });

    this.element.addEventListener('pointermove', (event) => {
      this.pointer.x = event.clientX;
      this.pointer.y = event.clientY;
      this.setHoveredFrom(event.target);
    });
    this.element.addEventListener('pointerleave', () => this.setHovered(-1));
    this.element.addEventListener('keydown', (event) => this.onKeyDown(event));

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.canvas);
    this.resizeObserver.observe(this.element);
  }

  onKeyDown(event) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.prev();
    }
  }

  setHovered(index) {
    this.hovered = index;
  }

  setHoveredFrom(target) {
    const item = target && target.closest ? target.closest('.model_carousel__item') : null;
    this.setHovered(item ? this.items.indexOf(item) : -1);
  }

  // Après une animation, une autre case peut se retrouver sous la souris immobile
  syncHover() {
    this.setHoveredFrom(document.elementFromPoint(this.pointer.x, this.pointer.y));
  }

  // On ne dessine que quand la section est visible à l'écran
  observe() {
    this.observer = new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
    });
    this.observer.observe(this.section);
  }
}
